import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import {
  MdAdminPanelSettings,
  MdOutlineGroups,
  MdOutlineGroups2,
  MdOutlinePaid,
  MdOutlineVerifiedUser,
} from 'react-icons/md';
import { CiHome } from 'react-icons/ci';
import { SidebarFooter } from './SidebarFooter';
import { Menu, Transition } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { LineChartIcon } from 'lucide-react';
import { FaCcVisa } from 'react-icons/fa';
import { File } from 'react-feather';

const adminNavItems = [
  { to: '/app', label: 'Home', icon: <CiHome size={22} /> },
  {
    to: '/app/administration',
    label: 'Administration',
    icon: <MdAdminPanelSettings size={22} />,
  },
  { to: '/app/finance', label: 'Finance', icon: <MdOutlinePaid size={22} /> },
  {
    to: '/app/analytics',
    label: 'Analytics',
    icon: <LineChartIcon size={22} />,
  },
  { to: '/app/users', label: 'Users', icon: <MdOutlineGroups2 size={22} /> },
  {
    to: '/app/transactions',
    label: 'Transactions',
    icon: <MdOutlineVerifiedUser size={22} />,
  },
  { to: '/app/kyc', label: 'KYC Console', icon: <MdOutlineGroups size={22} /> },
  // { to: '/app/api-logs', label: 'API Logs', icon: <File size={22} /> },
];

const developerNavItems = [
  { to: '/app/dev/overview', label: 'Overview', icon: <CiHome size={22} /> },
  {
    to: '/app/dev/projects',
    label: 'Projects',
    icon: <MdOutlineGroups size={22} />,
  },
  { to: '/app/dev/tasks', label: 'Tasks', icon: <MdOutlinePaid size={22} /> },
  {
    to: '/app/dev/timeline',
    label: 'Timeline',
    icon: <MdOutlineVerifiedUser size={22} />,
  },
  {
    to: '/app/dev/notes',
    label: 'Notes',
    icon: <MdOutlineGroups2 size={22} />,
  },
  {
    to: '/app/dev/subscriptions',
    label: 'Subscriptions',
    icon: <MdAdminPanelSettings size={22} />,
  },
  {
    to: '/app/dev/changelog',
    label: 'Changelog',
    icon: <MdOutlineVerifiedUser size={22} />,
  },
];

const vaultNavItems = [
  {
    label: 'Structure',
    children: [
      { to: '/vault/overview', label: 'Overview' }, // ← Injected
      { to: '/vault/access', label: 'Access Control' },
      { to: '/vault/master', label: 'Master Vault' },
      { to: '/vault/branches', label: 'Branch Vaults' },
    ],
  },
  {
    label: 'Float Movement',
    children: [
      { to: '/vault/float-allocation', label: 'Float Allocation' },
      { to: '/vault/replenishment', label: 'Auto-Replenishment' },
    ],
  },
  {
    label: 'Incident Management',
    children: [
      { to: '/vault/incidents', label: 'View Incidents' },
      { to: '/vault/incident-settings', label: 'Incident Settings' },
    ],
  },
];

const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mode, setMode] = useState(Cookies.get('admin_mode') || 'admin');
  const [showModeMenu, setShowModeMenu] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const location = useLocation();
  const navigate = useNavigate();

  const toggleSection = (label) => {
    setOpenSections((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    Cookies.set('admin_mode', newMode, { expires: 365 });
    setShowModeMenu(false);

    // Auto redirect
    if (newMode === 'developer') navigate('/app/dev/overview');
    else if (newMode === 'vault') navigate('/vault/overview');
    else navigate('/app');
  };

  const navItems =
    mode === 'developer'
      ? developerNavItems
      : mode === 'vault'
      ? vaultNavItems
      : adminNavItems;

  return (
    <aside
      className={`
        h-screen sticky top-0 z-20 flex flex-col justify-between border-r transition-all duration-300 flex-shrink-0 min-w-[5rem]
        ${collapsed ? 'w-20' : 'w-64'}
        ${
          mode === 'developer' &&
          'bg-[#273e1a] border-[#273e1a]/10 text-indigo-100'
        }
        ${
          mode === 'admin' &&
          'backdrop-blur-md bg-white/10 dark:bg-white/5 border-white/10 text-white'
        }
        ${mode === 'vault' && 'bg-[#430c3b] border-[#430c3b]/10 text-white'}
      `}
    >
      <div className="relative px-4 py-4 flex justify-between items-center">
        <Menu as="div" className="relative z-50">
          {({ open }) => (
            <>
              <Menu.Button>
                <div
                  className="text-lg font-bold tracking-tight cursor-pointer select-none"
                  onClick={() => setShowModeMenu(!showModeMenu)}
                >
                  {!collapsed && mode === 'admin' && 'Overwatch'}
                  {!collapsed && mode === 'developer' && 'Wirelab'}
                  {!collapsed && mode === 'vault' && 'Vault Layer'}
                </div>
              </Menu.Button>
              <Transition
                show={open}
                as={React.Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items
                  static
                  className={`
                    origin-top-right absolute left-0 mt-2 w-52 rounded-2xl 
                    ${
                      mode === 'developer' &&
                      'bg-[#273e1a] border-[#273e1a]/10 text-indigo-100'
                    }
                    ${
                      mode === 'admin' &&
                      'bg-gradient-to-b from-blue-700 to-blue-900 dark:from-gray-800 dark:to-gray-900 border-white/10 text-white'
                    }
                    ${
                      mode === 'vault' &&
                      'bg-[#430c3b] border-[#430c3b]/10 text-white'
                    }
                     p-2 backdrop-blur-xl shadow-xl ring-1 ring-white/20 border border-white/10 text-white
                    `}
                >
                  {['admin', 'developer', 'vault'].map((item) => (
                    <Menu.Item key={item}>
                      {() => (
                        <button
                          onClick={() => handleModeChange(item)}
                          className={`w-full text-left px-2 py-2 rounded-xl mb-2 hover:bg-white/10 ${
                            mode === item ? 'bg-white/10' : ''
                          }`}
                        >
                          {item === 'admin'
                            ? 'Overwatch'
                            : item === 'developer'
                            ? 'Wirelab'
                            : 'Vault Layer'}
                        </button>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </Transition>
            </>
          )}
        </Menu>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1 hover:bg-white/10 rounded transition"
        >
          {collapsed ? '»' : '«'}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 space-y-1">
        {mode !== 'vault'
          ? navItems.map(({ to, label, icon }) => {
              const isActive = location.pathname === to;
              return (
                <Link
                  key={to}
                  to={to}
                  className={`flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                    isActive
                      ? mode === 'developer'
                        ? 'bg-[#335222] font-semibold text-indigo-100'
                        : 'bg-white/10 font-semibold text-white'
                      : mode === 'developer'
                      ? 'text-indigo-200 hover:bg-[#335222]'
                      : 'text-white/70 hover:bg-white/10'
                  }`}
                >
                  <span className="flex-shrink-0">{icon}</span>
                  <span
                    className={`${
                      collapsed ? 'opacity-0 w-0' : 'opacity-100 w-auto'
                    } transition-opacity duration-300`}
                  >
                    {label}
                  </span>
                </Link>
              );
            })
          : navItems.map(({ label, children }) => (
              <div key={label} className="space-y-1">
                <button
                  onClick={() => toggleSection(label)}
                  className="flex w-full justify-between items-center px-4 py-2 font-medium rounded-lg hover:bg-white/10"
                >
                  <span>{label}</span>
                  <span>{openSections[label] ? '−' : '+'}</span>
                </button>
                <AnimatePresence initial={false}>
                  {openSections[label] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 space-y-1 overflow-hidden"
                    >
                      {children.map(({ to, label }) => (
                        <Link
                          key={to}
                          to={to}
                          className={`block px-4 py-1.5 rounded-md text-sm transition-all hover:bg-white/10 ${
                            location.pathname === to
                              ? 'bg-white/10 font-semibold'
                              : 'text-white/70'
                          }`}
                        >
                          {label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
      </nav>

      <SidebarFooter collapsed={collapsed} />
    </aside>
  );
};

export { Sidebar };
